import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { App as AntdApp } from 'antd';

import { TenantThemeProvider } from '@/theme/TenantThemeProvider';
import { App } from './App';

import '@/styles/tokens.css';
import '@/styles/global.css';

// R3-Sprint12-A-UX8:Vite dynamic import 加载老 chunk hash 失败兜底(标签页长开 +
// 部署后,旧 index.html 仍指向已删除的旧 chunk → "Failed to fetch dynamically
// imported module")。捕一次 → 自动 reload 拉新 index.html。
// 用 sessionStorage 防 reload 循环(若 reload 后再失败,可能真是网络问题不再 auto)
function _handleChunkLoadError(event: { message?: string; reason?: { message?: string } }) {
  const msg = event.message || event.reason?.message || '';
  if (
    /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(msg)
  ) {
    if (sessionStorage.getItem('__chunk_reload_attempted') === '1') {
      // 已 reload 过一次仍失败 — 不再循环,留报错给用户看
      return;
    }
    sessionStorage.setItem('__chunk_reload_attempted', '1');
    // eslint-disable-next-line no-console
    console.warn('chunk load failed, auto-reload to fetch new index.html');
    window.location.reload();
  }
}
window.addEventListener('error', _handleChunkLoadError);
window.addEventListener('unhandledrejection', (e) =>
  _handleChunkLoadError({ reason: e.reason as { message?: string } }),
);
// 成功加载完任何路由 5s 后清掉 flag,允许将来再次自愈
setTimeout(() => sessionStorage.removeItem('__chunk_reload_attempted'), 5000);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found in index.html');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TenantThemeProvider>
        <AntdApp>
          <App />
        </AntdApp>
      </TenantThemeProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>,
);
